import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { apiRootSelector } from '@schir-int-client/api-root';
import { doNothingAction } from '@schir-int-client/ngrx-helpers';
import { AppNotificationService, DialogService } from '@schir-int-client/tech';
import { userSelector } from '@schir-int-client/user-shared';
import { isEmpty } from 'lodash-es';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
	addZaehlblattAktionenAction,
	askToDeleteVerfahrenAction,
	askToMarkVerfahrenAsAbgelehntAction,
	askToUnassignKontaktAction,
	assignUSignalAction,
	createVerfahrenAction,
	createVerfahrenFailAction,
	createVerfahrenSuccessAction,
	deleteVerfahrenAction,
	deleteZaehlblattAktionAction,
	downloadAmtlicherAuszugAction,
	downloadAmtlicherAuszugSuccessAction,
	downloadAusdruckAction,
	downloadAusdruckSuccessAction,
	downloadSchiffsbriefAction,
	downloadSchiffsbriefSuccessAction,
	downloadSchiffszertifikatAction,
	downloadSchiffszertifikatSuccessAction,
	exportVerfahrenAction,
	exportVerfahrenSuccessAction,
	getLastOpenedVerfahrenAction,
	loadVerfahrenAction,
	loadVerfahrenSingleAction,
	loadVerfahrenSingleSuccessAction,
	loadVerfahrenSuccessAction,
	loadZaehlblattAction,
	loadZaehlblattSuccessAction,
	markVerfahrenAsAbgelehntAction,
	reloadSearchResultAction,
	reportNoUSignalLeftAction,
	searchForVerfahrenAction,
	searchForVerfahrenSuccessAction,
	setVerfahrenPropertyAction,
	setZugeordneteVerfahrenAction,
	updateVerfahrenNotizAction,
	updateVerfahrenSuccessAction,
	verfahrenBearbeitenAction,
	verfahrenBearbeitenBeendenAction,
	verfahrenUebernehmenAction,
} from './verfahren.actions';
import { VerfahrenFacade } from './verfahren.facade';
import { VerfahrenMessages } from './verfahren.messages';
import { verfahrenSearchBySelector, verfahrenSelector, verfahrenSingleSelector } from './verfahren.selectors';
import { VerfahrenService } from './verfahren.service';
import { Router } from '@angular/router';
import {
	AktenzeichenRenderer,
	AktenzeichenRendererProvider,
	AktenzeichenUtil,
} from '@schir-int-client/aktenzeichen-shared';
import { AppState } from '../../../../apps/int-client/src/app/shared/store/app-state.model';
import { getId } from '../../../test-helpers/src/lib/resource-helper';
import { getLink } from '@ngxp/rest';
import { setKontaktAssignmentAction } from '@schir-int-client/adressverwaltung-shared';

@Injectable()
export class VerfahrenEffects {
	private renderer: AktenzeichenRenderer;

	constructor(
		private actions: Actions,
		private service: VerfahrenService,
		private store: Store<AppState>,
		private notificationService: AppNotificationService,
		private verfahrenFacade: VerfahrenFacade,
		private dialogService: DialogService,
		private router: Router,
		private rendererProvider: AktenzeichenRendererProvider
	) {
		this.renderer = this.rendererProvider.getRenderer();
	}

	loadVerfahren = createEffect(() =>
		this.actions.pipe(
			ofType(loadVerfahrenAction, createVerfahrenSuccessAction),
			withLatestFrom(this.store.select(apiRootSelector), this.store.select(verfahrenSearchBySelector)),
			switchMap(([, apiRoot, query]) => {
				if (isEmpty(query.searchString)) {
					return this.service
						.getAll(apiRoot)
						.pipe(mergeMap(verfahrenList => [loadVerfahrenSuccessAction({ verfahrenList })]));
				}
				return of(searchForVerfahrenAction({ query, keepVerfahrenSingle: false }));
			})
		)
	);

	loadVerfahrenSingle = createEffect(() =>
		this.actions.pipe(
			ofType(loadVerfahrenSingleAction),
			switchMap(({ verfahrenUri }) =>
				this.service
					.getOne(verfahrenUri)
					.pipe(map(verfahren => loadVerfahrenSingleSuccessAction({ verfahren })))
			)
		)
	);

	searchVerfahren = createEffect(() =>
		this.actions.pipe(
			ofType(searchForVerfahrenAction),
			withLatestFrom(this.store.select(apiRootSelector)),
			switchMap(([{ query, keepVerfahrenSingle }, apiRoot]) =>
				this.service
					.searchVerfahren(apiRoot, query)
					.pipe(
						map(verfahrenList =>
							searchForVerfahrenSuccessAction({ verfahrenList, query, keepVerfahrenSingle })
						)
					)
			)
		)
	);

	getLastOpenedVerfahren = createEffect(() =>
		this.actions.pipe(
			ofType(getLastOpenedVerfahrenAction),
			withLatestFrom(this.store.select(apiRootSelector)),
			switchMap(([{ keepVerfahrenSingle, query }, apiRoot]) => {
				return this.service.getLastOpenedVerfahren(apiRoot).pipe(
					map(verfahrenList =>
						searchForVerfahrenSuccessAction({
							verfahrenList,
							query: query,
							keepVerfahrenSingle,
						})
					)
				);
			})
		)
	);

	createVerfahren = createEffect(() =>
		this.actions.pipe(
			ofType(createVerfahrenAction),
			withLatestFrom(this.store.select(verfahrenSelector)),
			switchMap(([{ newVerfahren }, verfahrenList]) =>
				this.service.createVerfahren(newVerfahren, verfahrenList.resource).pipe(
					map(newVerfahren => {
						this.verfahrenFacade.setSelectedVerfahren(newVerfahren);
						this.router.navigate(['/verfahren', getId(newVerfahren)]);
						const aktenzeichen = newVerfahren.schnellzugriff.find(
							({ key }) => key === 'common.short_aktenzeichen'
						);
						this.adaptSearchString(aktenzeichen.value);
						return doNothingAction();
					}),
					catchError(error => {
						if (
							error.issues &&
							(error.issues[0].messageCode == 'verfahren.duplicate.aktenzeichen' ||
								error.issues[0].messageCode == 'unauthorized.register')
						) {
							return of(createVerfahrenFailAction({ error }));
						} else {
							return of(error);
						}
					})
				)
			)
		)
	);

	createVerfahrenFail = createEffect(
		() =>
			this.actions.pipe(
				ofType(createVerfahrenFailAction),
				tap(({ error }) => {
					if (error.issues[0].messageCode === 'verfahren.duplicate.aktenzeichen') {
						this.notificationService.handleInfo(
							'Das Aktenzeichen ist bereits vergeben, das neue Verfahren wurde nicht angelegt.',
							null,
							'Doppeltes Aktenzeichen'
						);
					} else if (error.issues[0].messageCode === 'unauthorized.register') {
						this.notificationService.handleInfo(
							'Sie haben keinen Zugriff auf das ausgewählte Register.',
							null,
							'Kein Registerzugriff'
						);
					}
				})
			),
		{ dispatch: false }
	);

	acknowledgeVerfahrenCreated = createEffect(
		() =>
			this.actions.pipe(
				ofType(createVerfahrenSuccessAction),
				tap(() => {
					this.notificationService.handleInfo(VerfahrenMessages.ACKNOWLEDGE_VERFAHREN_CREATED);
				})
			),
		{ dispatch: false }
	);

	saveNotiz = createEffect(() =>
		this.actions.pipe(
			ofType(updateVerfahrenNotizAction),
			switchMap(({ notiz, verfahrenUri }) =>
				this.service
					.updateNotiz(notiz, verfahrenUri)
					.pipe(map(verfahren => updateVerfahrenSuccessAction({ verfahren })))
			)
		)
	);

	setProperty = createEffect(() =>
		this.actions.pipe(
			ofType(setVerfahrenPropertyAction),
			switchMap(({ verfahren, linkRel, value }) =>
				this.service
					.setVerfahrenProperty(verfahren, linkRel, value)
					.pipe(map(verfahren => updateVerfahrenSuccessAction({ verfahren })))
			)
		)
	);

	setZugeordneteVerfahren = createEffect(() =>
		this.actions.pipe(
			ofType(setZugeordneteVerfahrenAction),
			switchMap(({ verfahren, zugeordneteVerfahren }) => {
				return this.service
					.setZugeordneteVerfahren(verfahren, zugeordneteVerfahren)
					.pipe(map(() => loadVerfahrenSingleAction({ verfahrenUri: getLink(verfahren).href })));
			})
		)
	);

	addZaehlblattAktionen = createEffect(() =>
		this.actions.pipe(
			ofType(addZaehlblattAktionenAction),
			switchMap(({ zaehlblatt, values }) =>
				this.service
					.addZaehlblattAktionen(values, zaehlblatt)
					.pipe(map(zaehlblatt => loadZaehlblattSuccessAction({ zaehlblatt })))
			)
		)
	);

	deleteZaehlblattAktion = createEffect(() =>
		this.actions.pipe(
			ofType(deleteZaehlblattAktionAction),
			switchMap(({ entry }) =>
				this.service
					.deleteZaehlblattAktion(entry)
					.pipe(map(zaehlblatt => loadZaehlblattSuccessAction({ zaehlblatt })))
			)
		)
	);

	loadZaehlblatt = createEffect(() =>
		this.actions.pipe(
			ofType(loadZaehlblattAction),
			switchMap(({ verfahren }) =>
				this.service
					.loadZaehlblatt(verfahren)
					.pipe(map(zaehlblatt => loadZaehlblattSuccessAction({ zaehlblatt })))
			)
		)
	);

	confirmDelete = createEffect(() =>
		this.actions.pipe(
			ofType(askToDeleteVerfahrenAction),
			switchMap(({ verfahren }) =>
				this.dialogService
					.openConfirmationDialog(VerfahrenMessages.ASK_TO_DELETE_VERFAHREN)
					.pipe(map(confirmed => (confirmed ? deleteVerfahrenAction({ verfahren }) : doNothingAction())))
			)
		)
	);

	delete = createEffect(() =>
		this.actions.pipe(
			ofType(deleteVerfahrenAction),
			switchMap(({ verfahren }) =>
				this.service.delete(verfahren).pipe(map(verfahrenList => loadVerfahrenSuccessAction({ verfahrenList })))
			)
		)
	);

	confirmMarkAsAbgelehnt = createEffect(() =>
		this.actions.pipe(
			ofType(askToMarkVerfahrenAsAbgelehntAction),
			switchMap(({ verfahren }) =>
				this.dialogService
					.openConfirmationDialog(VerfahrenMessages.ASK_TO_REJECT_VERFAHREN)
					.pipe(
						map(confirmed =>
							confirmed ? markVerfahrenAsAbgelehntAction({ verfahren }) : doNothingAction()
						)
					)
			)
		)
	);

	markAsAbgelehnt = createEffect(() =>
		this.actions.pipe(
			ofType(markVerfahrenAsAbgelehntAction),
			switchMap(({ verfahren }) =>
				this.service
					.markAsAbgelehnt(verfahren)
					.pipe(map(updated => updateVerfahrenSuccessAction({ verfahren: updated })))
			)
		)
	);
	//DRUCK

	downloadAusdruck = createEffect(() =>
		this.actions.pipe(
			ofType(downloadAusdruckAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			switchMap(([{ ausdruckType }, verfahren]) =>
				this.service
					.downloadAusdruck(ausdruckType, verfahren)
					.pipe(map(data => downloadAusdruckSuccessAction({ data })))
			)
		)
	);

	saveAusdruck = createEffect(
		() =>
			this.actions.pipe(
				ofType(downloadAusdruckSuccessAction),
				tap(({ data }) => this.service.saveFile(data, 'RegisterAusdruck.pdf'))
			),
		{ dispatch: false }
	);

	downloadAmtlicherAuszug = createEffect(() =>
		this.actions.pipe(
			ofType(downloadAmtlicherAuszugAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			switchMap(([, verfahren]) =>
				this.service
					.downloadAmtlicherAuszug(verfahren)
					.pipe(map(data => downloadAmtlicherAuszugSuccessAction({ data })))
			)
		)
	);

	downloadSchiffszertifikatAction = createEffect(() =>
		this.actions.pipe(
			ofType(downloadSchiffszertifikatAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			switchMap(([, verfahren]) =>
				this.service
					.downloadSchiffszertifikat(verfahren)
					.pipe(map(data => downloadSchiffszertifikatSuccessAction({ data })))
			)
		)
	);

	downloadSchiffsbrief = createEffect(() =>
		this.actions.pipe(
			ofType(downloadSchiffsbriefAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			switchMap(([, verfahren]) =>
				this.service
					.downloadSchiffsbrief(verfahren)
					.pipe(map(data => downloadSchiffsbriefSuccessAction({ data })))
			)
		)
	);

	saveSchiffsbrief = createEffect(
		() =>
			this.actions.pipe(
				ofType(downloadSchiffsbriefSuccessAction),
				tap(({ data }) => this.service.saveFile(data, 'Schiffsbrief.pdf'))
			),
		{ dispatch: false }
	);

	saveAmtlicherAuszug = createEffect(
		() =>
			this.actions.pipe(
				ofType(downloadAmtlicherAuszugSuccessAction),
				tap(({ data }) => this.service.saveFile(data, 'AmtlicherAuszug.pdf'))
			),
		{ dispatch: false }
	);

	exportVerfahren = createEffect(() =>
		this.actions.pipe(
			ofType(exportVerfahrenAction),
			switchMap(({ url }) =>
				this.service.exportVerfahren(url).pipe(map(data => exportVerfahrenSuccessAction({ data })))
			)
		)
	);

	saveVerfahren = createEffect(
		() =>
			this.actions.pipe(
				ofType(exportVerfahrenSuccessAction),
				tap(({ data }) => this.service.saveFile(data, 'Verfahren.json'))
			),
		{ dispatch: false }
	);

	saveSchiffszertifikat = createEffect(
		() =>
			this.actions.pipe(
				ofType(downloadSchiffszertifikatSuccessAction),
				tap(({ data }) => this.service.saveFile(data, 'Schiffszertifikat.pdf'))
			),
		{ dispatch: false }
	);

	bearbeiten = createEffect(() =>
		this.actions.pipe(
			ofType(verfahrenBearbeitenAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector), this.store.select(userSelector)),
			switchMap(([, verfahren, user]) =>
				this.service
					.verfahrenBearbeiten(verfahren, user)
					.pipe(map(updatedVerfahren => updateVerfahrenSuccessAction({ verfahren: updatedVerfahren })))
			)
		)
	);

	uebernehmen = createEffect(() =>
		this.actions.pipe(
			ofType(verfahrenUebernehmenAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector), this.store.select(userSelector)),
			switchMap(([, verfahren, user]) =>
				this.service
					.verfahrenUebernehmen(verfahren, user)
					.pipe(map(updatedVerfahren => updateVerfahrenSuccessAction({ verfahren: updatedVerfahren })))
			)
		)
	);

	bearbeitenBeenden = createEffect(() =>
		this.actions.pipe(
			ofType(verfahrenBearbeitenBeendenAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			switchMap(([, verfahren]) => {
				return this.service
					.verfahrenBearbeitenBeenden(verfahren)
					.pipe(map(updatedVerfahren => updateVerfahrenSuccessAction({ verfahren: updatedVerfahren })));
			})
		)
	);

	confirmUnassignKontakt = createEffect(() =>
		this.actions.pipe(
			ofType(askToUnassignKontaktAction),
			switchMap(({ verfahrenKontakt }) =>
				this.dialogService
					.openConfirmationDialog(VerfahrenMessages.ASK_TO_UNASSIGN_KONTAKT)
					.pipe(
						map(confirmed =>
							confirmed ? setKontaktAssignmentAction({ verfahrenKontakt }) : doNothingAction()
						)
					)
			)
		)
	);

	assignUSignalForVerfahren = createEffect(() =>
		this.actions.pipe(
			ofType(assignUSignalAction),
			switchMap(({ verfahren }) =>
				this.service.assignUsignal(verfahren).pipe(
					map(usignal => {
						const aktenzeichen = this.renderer.render(AktenzeichenUtil.parse(verfahren));
						this.notificationService.handleInfo(
							`Das U-Signal "${usignal.body.signal}" wurde dem Verfahren "${aktenzeichen}" zugeordnet.`
						);
						return reloadSearchResultAction();
					}),
					catchError(error => of(reportNoUSignalLeftAction({ payload: error })))
				)
			)
		)
	);

	private adaptSearchString(searchString: string) {
		this.verfahrenFacade.verfahrenCreatedSubject?.next(searchString);
	}
}
